import CheckCircle from "assets/images/settings/checkCircle.svg";
import CheckCircleWhite from "assets/images/settings/checkCircleWhite.svg";
import _ from "lodash";
import React from "react";
import styled from "styled-components";
import { colors } from "../../../utils";
import Button from "../../Button";
import TextView from "../../TextView";
import EssentialPackageIcon from "@assets/images/packages/essentialPackageIcon.svg";
import FreePackageIcon from "@assets/images/packages/FreePackage.svg";
import PremiumPackageIcon from "@assets/images/packages/premiumPackageIcon.svg";
import ProSuiteIcon from "@assets/images/ProSuite.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CoinIcon from "@assets/images/segmentEditor/coin.svg";
import CoinsIcon from "@assets/images/segmentEditor/coins.svg";
import { StyledTooltip } from "../../IyugaTooltip";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal as MUIModal, Typography } from "@mui/material";
import Coins3dIcon from "@assets/images/Coins3d.svg";

import { TIER_FEATURES } from "../../../constants/subscriptionFeatures";

// Styled Components
const Container = styled.div`
  border-radius: 8px;
  background-color: ${colors.background};
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0px 0px 5.9px 0px #00000040;
  border: ${(props) =>
    props.isInfinity ? `2px solid ${colors.yellowDark}` : "none"};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px;
  padding-bottom: 0px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  overflow: hidden; /* prevents wrap from changing height */
  white-space: nowrap;
`;

const DiscountBadge = styled.div`
  background-color: #e87033;
  padding: 5px 10px;
  border-radius: 4px;
  width: max-content;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0px 2px 0px 9px; /* Reduced horizontal padding */
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;

  svg {
    font-size: 16px;
    color: #29c18c;
  }
`;

const FeaturesLink = styled(TextView)`
  color: ${(props) => (props.isActive ? colors.white : colors.PrimaryPurple)};
  display: flex;
  border-top: ${(props) =>
    props.isActive
      ? `1px solid ${colors.white}`
      : `1px solid ${colors.lightGrey}`};
  padding: 0px 10px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  height: 60px;
  @media (max-width: 600px) {
    display: none;
  }
`;

const AnnualPrice = styled.div`
  display: flex;
  align-self: center;
  align-items: end;
  justify-content: center;
  width: 100%;
  background-color: ${colors.background};
  gap: 5px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.isMonthly ? 4 : 5)};
  -webkit-box-orient: vertical;
  height: ${(props) => (props.isMonthly ? "65px" : "75px")};
`;

export const PriceContainer = styled.div`
  height: 74px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
`;

export const AnnualPriceContainer = styled.div`
  height: 100px;
`;

export const FeatureItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1; /* Allow it to grow and take available space */
`;

const EarningsProgramContainer = styled.div`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  padding: 8px;
  background: #d4e2f280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  align-self: center;
`;

const EarningsProgramWrapper = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const BestDealContainer = styled.div`
  background-color: #f5cc3b;
  padding: 2px 8px;
  border-radius: 16px;
  color: #123051;
  font-weight: 700;
`;

const AnnualPriceValue = styled.div``;

export default function SubscriptionItem({
  item,
  onViewFeatures,
  onActionPress,
  showFeatures = true,
  style,
  isAnnually,
}) {
  const {
    name,
    body,
    per_month_cost,
    discount,
    short_description,
    is_active,
    action,
    sub_tier,
  } = item;

  const activeItem = is_active || action === "ACTIVE";

  const currentTierFeatures = TIER_FEATURES.find(
    (tier) => tier.sub_tier === sub_tier
  );

  const featureItems = currentTierFeatures ? currentTierFeatures.features : [];

  const [openEarningsModal, setOpenEarningsModal] = React.useState(false);

  const handleOpenEarningsModal = () => {
    setOpenEarningsModal(true);
  };

  const handleCloseEarningsModal = () => {
    setOpenEarningsModal(false);
  };

  const getButtonText = () => {

    if (activeItem) return `Switch to ${isAnnually ? "Monthly" : "Annual"}`;



    if (sub_tier === 0) return "Downgrade to Free";

    return `${
      action?.charAt(0).toUpperCase() + action?.toLowerCase().slice(1)
    } to ${name}`;
  };

  return (
    <Container
      style={{
        backgroundColor: activeItem ? colors.purpleGradient : "transparent",
        height: !showFeatures
          ? "340px"
          : "100%" /* Set height to 100% to fill parent */,
        borderRadius: !showFeatures ? "0px" : "8px",
        ...style,
      }}
    >
      <Header>
        <TitleRow>
          <TextView
            variant="subtitle"
            style={{
              fontWeight: 700,
              ...(activeItem && { color: colors.white }),
            }}
          >
            {name}
          </TextView>
          {sub_tier === 0 && (
            <img
              src={FreePackageIcon}
              alt="Free Tier Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {sub_tier === 1 && (
            <img
              src={EssentialPackageIcon}
              alt="Essential Tier Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {sub_tier === 2 && (
            <>
              <img
                src={PremiumPackageIcon}
                alt="Premium Tier Icon"
                style={{ width: "20px", height: "20px" }}
              />
              <BestDealContainer>
                <TextView style={{ fontWeight: 700 }}>Most Popular</TextView>
              </BestDealContainer>
            </>
          )}
          {sub_tier === 3 && (
            <img
              src={ProSuiteIcon}
              alt="Pro Suite Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
        </TitleRow>
        <Description isMonthly={!isAnnually}>
          <TextView style={{ color: activeItem ? colors.white : "#565556" }}>
            {short_description}
          </TextView>
        </Description>
        <EarningsProgramWrapper>
          {(sub_tier === 2 || sub_tier === 3) && (
            <EarningsProgramContainer>
              <img
                src={sub_tier === 2 ? CoinIcon : CoinsIcon}
                alt="Earnings Icon"
                style={{
                  width: sub_tier === 3 ? "36px" : "24px",
                  height: sub_tier === 3 ? "24px" : "24px",
                }}
              />
              <TextView
                style={{
                  fontFamily: "DM Sans",
                  fontWeight: 700,
                  fontStyle: "Bold",
                  fontSize: "12px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "#123051",
                }}
              >
                Earnings Program
              </TextView>
              <StyledTooltip
                title={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px", // Reduced gap
                      padding: "15px", // Reduced padding
                      minWidth: "400px",
                      // maxWidth: "400px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={Coins3dIcon}
                        alt="Earnings Program Icon"
                        style={{ width: "28px", height: "28px" }} // Reduced icon size
                      />
                      <TextView
                        style={{
                          fontFamily: "DM Sans",
                          fontWeight: 700,
                          fontStyle: "Bold",
                          fontSize: "16px", // Reduced font size
                          lineHeight: "1.2",
                          letterSpacing: "0%",
                          color: "#363636",
                          height: "auto",
                          textAlign: "center",
                        }}
                      >
                        Earnings Program
                      </TextView>
                    </div>
                    <TextView style={{ fontWeight: 700, fontSize: "14px" }}>
                      🔗 Effortless Referrals
                    </TextView>
                    <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                      Anyone who subscribes while viewing your Wheels or Gallery
                      will be automatically linked to your Referral Code.
                    </TextView>
                    <TextView style={{ fontWeight: 700, fontSize: "14px" }}>
                      💸 Lifetime Earnings
                    </TextView>
                    <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                      As long as you maintain a Premium (or higher) subscription
                      & your referral remains a paying subscriber, you'll earn
                      every month.
                    </TextView>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={PremiumPackageIcon}
                        alt="Premium Plan Icon"
                        style={{ width: "20px", height: "20px" }} // Reduced icon size
                      />
                      <TextView style={{ fontSize: "14px", color: "#363636" }}>
                        Premium Plan
                        <span style={{ fontWeight: 700 }}>→ 5% commission</span>
                      </TextView>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={ProSuiteIcon}
                        alt="ProSuite Plan Icon"
                        style={{ width: "20px", height: "20px" }} // Reduced icon size
                      />
                      <TextView
                        style={{
                          fontSize: "14px", // Reduced font size
                          color: "#363636",
                        }}
                      >
                        ProSuite Plan
                        <span style={{ fontWeight: 700 }}>
                          → 10% commission
                        </span>
                      </TextView>
                    </div>
                    <div
                      style={{
                        border: "1px solid #F79A00",
                        borderRadius: "8px",
                        padding: "8px", // Reduced padding
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px", // Reduced gap
                        backgroundColor: "#FCEFD8",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Reduced gap
                        }}
                      >
                        <TextView
                          style={{
                            fontWeight: 700,
                            fontSize: "14px", // Reduced font size
                            color: "#363636",
                          }}
                        >
                          🚨 Coming Soon:
                        </TextView>
                      </div>
                      <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                        <b>Paid Locks</b> – Enables you to charge for access to
                        your Wheels!
                      </TextView>
                    </div>
                  </div>
                }
                arrow
                placement="top"
                color={"white"}
                textColor={"#123051"}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    maxWidth: "600px !important",
                    width: "auto !important",
                    minWidth: "auto !important",
                    maxHeight: "auto", // Allow height to adjust to content
                    overflowY: "hidden", // Prevent vertical scrolling
                    overflowX: "hidden",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                    borderRadius: "8px",
                    padding: "10px",
                  },
                }}
              >
                <InfoOutlinedIcon
                  style={{
                    fontSize: "16px",
                    color: "#123051",
                    cursor: "pointer",
                  }}
                />
              </StyledTooltip>
            </EarningsProgramContainer>
          )}
        </EarningsProgramWrapper>
        <PriceContainer>
          {_.isEmpty(discount) ? null : (
            <TextView
              variant="subtitle"
              style={{
                textDecoration: "line-through",
                color: activeItem ? colors.white : colors.lightGrey,
              }}
            >
              $
              {Number.isInteger(discount?.discount_amount)
                ? discount?.discount_amount
                : Number(discount?.discount_amount).toFixed(2)}
            </TextView>
          )}

          {per_month_cost == 0 ? (
            <TextView
              variant="title"
              style={{ ...(activeItem && { color: colors.white }) }}
            >
              Free
            </TextView>
          ) : (
            <TextView>
              <span
                style={{
                  fontWeight: 700,
                  fontSize: "28px",
                  lineHeight: "21px",
                  ...(activeItem && { color: colors.white }),
                }}
              >
                $
                {!_.isEmpty(discount)
                  ? discount?.amount_after_discount
                  : Number.isInteger(per_month_cost)
                  ? per_month_cost
                  : Number(per_month_cost).toFixed(2)}
              </span>
              <span
                style={{
                  color: activeItem ? colors.white : "#565556",
                  marginLeft: "2px",
                }}
              >
                /month
              </span>
            </TextView>
          )}

          {isAnnually && per_month_cost !== 0 && (
            <AnnualPrice>
              <AnnualPriceValue>
                {!_.isEmpty(discount)
                  ? `$${Math.round(discount?.amount_after_discount * 12)}`
                  : `$${Math.round(per_month_cost * 12)}`}
              </AnnualPriceValue>
              <TextView variant="text" style={{ color: "#565556" }}>
                Billed annually
              </TextView>
            </AnnualPrice>
          )}
        </PriceContainer>
      </Header>

      <ButtonContainer>
        <Button
          onClick={!activeItem && onActionPress}
          variant={"outlined"}
          style={{
            width: "90%",
            margin: "auto",
            backgroundColor: activeItem
              ? colors.PrimaryPurple100
              : colors.white,
            cursor: activeItem ? "default" : "pointer",
          }}
        >
          {getButtonText()}
        </Button>
      </ButtonContainer>

      {showFeatures && (
        <FeatureItemContainer>
          <FeaturesList>
            {featureItems.map((feature, index) => (
              <FeatureItem key={index}>
                <img
                  src={activeItem ? CheckCircleWhite : CheckCircle}
                  alt="check"
                />
                <TextView
                  variant="text"
                  style={{
                    color: activeItem ? colors.white : "#565556",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: feature.feature_description,
                  }}
                />
                {typeof feature.tooltip === "string" && (
                  <StyledTooltip
                    title={
                      <div
                        style={{ textAlign: "left" }}
                        dangerouslySetInnerHTML={{ __html: feature.tooltip }}
                      ></div>
                    }
                    arrow
                    placement="top"
                    color={"#FCEFD8"}
                    textColor={"#123051"}
                    width={"206px"}
                  >
                    <InfoOutlinedIcon
                      style={{
                        fontSize: "16px",
                        color: activeItem ? colors.white : "#123051",
                        cursor: "pointer",
                      }}
                    />
                  </StyledTooltip>
                )}
              </FeatureItem>
            ))}
          </FeaturesList>
          <FeaturesLink
            variant="subtitle"
            onClick={onViewFeatures}
            isActive={activeItem}
          >
            {activeItem ? "Compare" : "View all Features"}
          </FeaturesLink>
        </FeatureItemContainer>
      )}
    </Container>
  );
}
